/**
 * PageShell — Task 3D skip-link repair.
 *
 * Structural layout for every page:
 *
 *   skip link           (rendered once at app root in main.tsx)
 *   PageShell
 *     ├── header/banner  (AppHeader, always outside main)
 *     ├── <main id="main" tabindex="-1>
 *     │     └── {children} (page content)
 *     └── <footer>       (PageFooter, always outside main)
 *
 * WCAG 2.4.1: a keyboard user who activates the skip link jumps to the
 * <main id="main"> element, NOT to the header. Without this layout the
 * header is inside main and the skip link is functionally useless — it
 * "skips" to the very element it was supposed to skip over.
 *
 * tabindex="-1" makes <main> programmatically focusable so the browser
 * moves keyboard focus to it on activation; without it, focus falls back
 * to <body> and the visible focus indicator is lost.
 *
 * Only one <main> element per page (HTML5 spec). Page-level error and
 * loading states should use the same shell — never a second <main>.
 */
import * as React from "react";

import AppHeader from "../ui/AppHeader";
import PageFooter from "../ui/PageFooter";

type PageShellProps = {
  children: React.ReactNode;
  /** Forwarded to the outer container (replaces the v5 <main id="main">). */
  className?: string;
  /** Optional id forwarded to <main>. Defaults to "main". */
  mainId?: string;
  /** Optional main element reference (for tests that need to focus it). */
  mainRef?: React.Ref<HTMLElement>;
};

export default function PageShell({
  children,
  className,
  mainId = "main",
  mainRef,
}: PageShellProps) {
  return (
    <div className={className}>
      <AppHeader />
      <main
        id={mainId}
        ref={mainRef as React.Ref<HTMLElement>}
        tabIndex={-1}
      >
        {children}
      </main>
      <PageFooter />
    </div>
  );
}
